import { useEffect, useRef, useState } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Music2 } from "lucide-react";
import api, { setToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const PRESETS = {
  pipeband: ["Cornemuse", "Caisse claire", "Ténor", "Basse", "Drum Major", "Accompagnant"],
  bagad: ["Cornemuse", "Bombarde", "Caisse claire", "Basse", "Percussion", "Penn-soner", "Autre", "Accompagnant"],
};

export default function LoginPage() {
  const { status, setAuth, lang, setLang, t, identity, memberships: ctxMemberships, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login");
  const [showPwd, setShowPwd] = useState(false);

  // login
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);

  // register
  const [regStep, setRegStep] = useState(1);
  const [spaceKind, setSpaceKind] = useState("band"); // band = groupe musical, school = enseignement
  const [groupName, setGroupName] = useState("");
  const [diminutif, setDiminutif] = useState("");
  const [style, setStyle] = useState("bagad");
  const [customStyle, setCustomStyle] = useState("");
  const [language, setLanguage] = useState("fr");
  const [logoFile, setLogoFile] = useState(null);
  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [instrument, setInstrument] = useState("");

  // nouvelle auth par e-mail (Phase 2) : vérification OTP + choix/création de groupe
  const [regEmail, setRegEmail] = useState("");
  const [regConfirm, setRegConfirm] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const otpRefs = useRef([]);
  const [resendIn, setResendIn] = useState(0);
  const [afterVerify, setAfterVerify] = useState(null); // "create-group" | null
  const [memberships, setMemberships] = useState([]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  // Reprise d'une session « identité » (rechargement) : sélecteur ou création de groupe
  useEffect(() => {
    if (identity) {
      setMemberships(ctxMemberships || []);
      setMode((ctxMemberships || []).length ? "choose" : "create-group");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity]);

  const switchLang = (l) => setLang(l);

  // Redirection post-login (liens QR : /connexion?next=/achats%3Fcat%3D...) — chemin interne uniquement
  const rawNext = searchParams.get("next");
  const safeNext = rawNext && rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : null;

  // Phase 3 — mot de passe oublié (hooks déclarés AVANT tout early-return)
  const [resetToken, setResetToken] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [newPwdConfirm, setNewPwdConfirm] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  // Lien de réinitialisation reçu par e-mail : /connexion?reset=<token>
  useEffect(() => {
    const rt = searchParams.get("reset");
    if (rt) {
      setResetToken(rt);
      setMode("reset");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "ok") return <Navigate to={safeNext || "/tableau-de-bord"} replace />;

  const finish = (data) => {
    setAuth(data);
    navigate(safeNext || "/tableau-de-bord");
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { login_id: loginId, password, remember });
      if (data.requires_verification) {
        setPendingEmail(loginId);
        setMaskedEmail(data.email);
        setResendIn(60);
        setAfterVerify(null);
        setOtpDigits(["", "", "", "", "", ""]);
        setMode("verify");
        return;
      }
      if (data.requires_migration) {
        // Ancien identifiant prénom@diminutif : plus aucun compte legacy n'existe en base
        setError(t("login.legacy_removed"));
        return;
      }
      if (data.identity_token) {
        // 0 ou 2+ memberships → session identité puis sélecteur / création de groupe
        setToken(data.identity_token);
        // lien d'invitation : honorer la cible (l'acceptation se fait en session identité)
        if (safeNext && safeNext.startsWith("/invitation/")) {
          navigate(safeNext);
          return;
        }
        setMemberships(data.memberships || []);
        setMode(data.no_membership ? "create-group" : "choose");
        return;
      }
      finish(data);
    } catch (err) {
      setError(err.friendly);
    } finally {
      setLoading(false);
    }
  };

  // Étape 1 nouveau parcours fondateur : création du compte RANNEA global (e-mail + mot de passe)
  const doRegisterAccount = async () => {
    setError("");
    if (regPassword !== regConfirm) {
      setError(t("param.pwd_mismatch"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { prenom, nom, email: regEmail, password: regPassword, lang });
      setPendingEmail(regEmail);
      setMaskedEmail(data.email);
      setResendIn(60);
      setAfterVerify("create-group");
      setOtpDigits(["", "", "", "", "", ""]);
      setMode("verify");
    } catch (err) {
      setError(err.friendly);
    } finally {
      setLoading(false);
    }
  };

  const onOtpChange = (i, v) => {
    const digit = v.replace(/\D/g, "").slice(-1);
    setOtpDigits((prev) => { const n = [...prev]; n[i] = digit; return n; });
    if (digit && i < 5) otpRefs.current[i + 1]?.focus();
  };

  const onOtpKeyDown = (i, e) => {
    if (e.key === "Backspace" && !otpDigits[i] && i > 0) otpRefs.current[i - 1]?.focus();
  };

  const doVerify = async (e) => {
    e.preventDefault();
    setError("");
    const code = otpDigits.join("");
    if (code.length !== 6) {
      setError(t("auth.code_incomplete"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-email", { email: pendingEmail, code });
      setToken(data.identity_token);
      toast.success(t("auth.email_verified"));
      if (afterVerify === "create-group") {
        await doCreateGroup();
        return;
      }
      setMemberships(data.memberships || []);
      setMode(data.no_membership ? "create-group" : "choose");
    } catch (err) {
      setError(err.friendly);
    } finally {
      setLoading(false);
    }
  };

  const doResend = async () => {
    setError("");
    try {
      await api.post("/auth/resend-verification", { email: pendingEmail });
      setResendIn(60);
      toast.success(t("auth.code_resent"));
    } catch (err) {
      setError(err.friendly);
    }
  };

  // Création du groupe avec l'identité vérifiée (token identité déjà dans le Bearer)
  const doCreateGroup = async () => {
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register-group", {
        group_name: groupName, diminutif,
        style: spaceKind === "school" ? "autre" : style,
        custom_style: spaceKind === "school" ? "Enseignement" : customStyle,
        language, prenom, nom, password: "", instrument,
        kind: spaceKind,
        disciplines: ["pipeband", "bagad"].includes(style) && spaceKind === "band" ? [style] : [],
      });
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        await api.post("/group/logo", fd).catch(() => {});
      }
      toast.success(t("login.group_created", { id: data.user.login_id }));
      finish(data);
    } catch (err) {
      setError(err.friendly);
      setLoading(false);
    }
  };

  const doSelect = async (m) => {
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/select", { membership_id: m.membership_id });
      finish(data);
    } catch (err) {
      setError(err.friendly);
      setLoading(false);
    }
  };

  const quitIdentitySession = () => {
    setMode("login");
    setMemberships([]);
    logout();
  };

  // Phase 3 — mot de passe oublié
  const doForgot = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: loginId, origin_url: window.location.origin });
      setForgotSent(true);  // réponse générique quelle que soit l'adresse (anti-énumération)
    } catch (err) {
      setError(err.friendly);
    } finally {
      setLoading(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setError("");
    if (newPwd !== newPwdConfirm) {
      setError(t("param.pwd_mismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token: resetToken, new_password: newPwd });
      toast.success(t("auth.reset_done"));
      setResetToken("");
      setNewPwd("");
      setNewPwdConfirm("");
      setMode("login");
      navigate("/connexion", { replace: true });
    } catch (err) {
      setError(err.friendly);
    } finally {
      setLoading(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register-group", {
        group_name: groupName, diminutif,
        style: spaceKind === "school" ? "autre" : style,
        custom_style: spaceKind === "school" ? "Enseignement" : customStyle,
        language, prenom, nom, password: regPassword, instrument,
        kind: spaceKind,
        disciplines: ["pipeband", "bagad"].includes(style) && spaceKind === "band" ? [style] : [],
      });
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        await api.post("/group/logo", fd).catch(() => {});
      }
      toast.success(t("login.group_created", { id: data.user.login_id }));
      finish(data);
    } catch (err) {
      setError(err.friendly);
    } finally {
      setLoading(false);
    }
  };

  const instruments = spaceKind === "school" ? [] : PRESETS[style] || [];

  return (
    <div className="min-h-screen app-bg flex flex-col md:flex-row" data-testid="login-page">
      {/* Panneau gauche */}
      <div
        className="md:w-2/5 sidebar-bg border-b md:border-b-0 md:border-r border-app p-6 md:p-10 flex md:flex-col items-center md:items-start justify-between gap-4"
      >
        <div className="flex items-center gap-3">
          <img src="/rannea-logo-white.png" alt="RANNEA — The link between musicians" className="h-14 w-auto" data-testid="login-brand-logo" />
        </div>
        <div className="hidden md:block">
          <h1 className="font-heading text-4xl font-extrabold leading-tight mb-4">
            {t("hero_title_1")}<br />{t("hero_title_2")}
          </h1>
          <p className="t-secondary text-sm leading-relaxed max-w-sm">
            {t("hero_sub")}
          </p>
        </div>
      </div>

      {/* Formulaire */}
      <div
        className="flex-1 flex items-center justify-center p-6"
      >
        <div className="w-full max-w-md">
          <div className="flex justify-end gap-1 mb-3" data-testid="language-switcher">
            {["fr", "en"].map((l) => (
              <button
                key={l}
                onClick={() => switchLang(l)}
                data-testid={`lang-${l}-button`}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors uppercase ${
                  lang === l ? "brand-bg text-white border-transparent" : "border-app t-secondary hover:bg-white/5"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <div className="flex gap-2 mb-6" data-testid="login-mode-tabs">
            {[
              { k: "login", l: t("tab_login"), tid: "tab-login" },
              { k: "register", l: t("tab_register"), tid: "tab-register" },
            ].map((t2) => (
              <button
                key={t2.k}
                onClick={() => { setMode(t2.k); setError(""); }}
                data-testid={t2.tid}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  mode === t2.k ? "brand-bg text-white" : "card-bg t-secondary border border-app hover:bg-white/5"
                }`}
              >
                {t2.l}
              </button>
            ))}
          </div>

          {error && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-rose-500/15 border border-rose-500/30 text-rose-400 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          {mode === "login" && (
            <form onSubmit={doLogin} className="app-card p-6 space-y-4 anim-fade-up" data-testid="login-form">
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("auth.email_label")}</Label>
                <Input type="email" inputMode="email" autoComplete="email" value={loginId} onChange={(e) => setLoginId(e.target.value)} required
                  placeholder={t("auth.email_ph")} className="mt-1 bg-transparent border-app" data-testid="login-id-input" />
              </div>
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("password_label")}</Label>
                <div className="relative mt-1">
                  <Input type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required
                    className="bg-transparent border-app pr-10" data-testid="login-password-input" />
                  <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 t-secondary" data-testid="toggle-password-visibility">
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm t-secondary cursor-pointer">
                  <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-emerald-500" data-testid="remember-me-checkbox" />
                  {t("remember_me")}
                </label>
                <button type="button" className="text-xs brand hover:underline"
                  onClick={() => { setError(""); setMode("forgot"); }}
                  data-testid="forgot-password-link">
                  {t("forgot_password")}
                </button>
              </div>
              <Button type="submit" disabled={loading} className="w-full brand-bg text-white hover:opacity-90" data-testid="login-submit-button">
                {loading ? t("connecting") : t("connect")}
              </Button>
            </form>
          )}

          {mode === "register" && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (regStep === 1) return;
                if (spaceKind === "school") doRegister(e); else doRegisterAccount();
              }}
              className="app-card p-6 space-y-4 anim-fade-up" data-testid="register-form">
              {regStep === 1 ? (
                <>
                  <h2 className="font-heading font-bold text-lg">{t(spaceKind === "personal" ? "group_creation_personal" : spaceKind === "school" ? "group_creation_school" : "group_creation")}</h2>
                  <div>
                    <Label className="t-secondary text-xs uppercase tracking-wider">{t("login.space_type_label")}</Label>
                    <div className="grid grid-cols-3 gap-2 mt-1" data-testid="register-space-type">
                      {[["band", "login.space_band", "login.space_band_desc", false], ["school", "login.space_school", "login.space_school_desc", true], ["personal", "login.space_personal", "login.space_personal_desc", false]].map(([k, l, d, locked]) => (
                        <button
                          type="button"
                          key={k}
                          disabled={locked}
                          onClick={() => !locked && setSpaceKind(k)}
                          className={`min-w-0 px-2 py-2.5 rounded-lg border text-left transition-colors ${
                            locked ? "border-app t-secondary opacity-60 cursor-not-allowed"
                            : spaceKind === k ? "brand-bg text-white border-transparent" : "border-app t-secondary hover:bg-white/5"
                          }`}
                          data-testid={`space-type-${k}`}
                        >
                          <span lang={lang} className="block text-[13px] font-semibold leading-tight break-words [hyphens:auto]">{t(l)}</span>
                          <span className={`block text-[10px] leading-snug break-words mt-0.5 ${spaceKind === k ? "text-white/80" : "t-secondary"}`}>{t(d)}</span>
                          {locked && <span className="inline-block mt-1 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400" data-testid="space-type-dev-badge">{t("login.space_dev")}</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                  {spaceKind !== "school" ? (
                    <>
                      <h3 className="t-secondary text-xs uppercase tracking-wider pt-1">{t("auth.your_account")}</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="t-secondary text-xs uppercase tracking-wider">{t("firstname")}</Label>
                          <Input value={prenom} onChange={(e) => setPrenom(e.target.value)} required className="mt-1 bg-transparent border-app" data-testid="register-prenom-input" />
                        </div>
                        <div>
                          <Label className="t-secondary text-xs uppercase tracking-wider">{t("lastname")}</Label>
                          <Input value={nom} onChange={(e) => setNom(e.target.value)} className="mt-1 bg-transparent border-app" data-testid="register-nom-input" />
                        </div>
                      </div>
                      <div>
                        <Label className="t-secondary text-xs uppercase tracking-wider">{t("auth.email_label")}</Label>
                        <Input type="email" inputMode="email" autoComplete="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} required
                          placeholder={t("auth.email_ph")} className="mt-1 bg-transparent border-app" data-testid="register-email-input" />
                      </div>
                      <div>
                        <Label className="t-secondary text-xs uppercase tracking-wider">{t("password_label")}</Label>
                        <Input type="password" autoComplete="new-password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} required minLength={8} className="mt-1 bg-transparent border-app" data-testid="register-password-input" />
                      </div>
                      <div>
                        <Label className="t-secondary text-xs uppercase tracking-wider">{t("confirmation")}</Label>
                        <Input type="password" autoComplete="new-password" value={regConfirm} onChange={(e) => setRegConfirm(e.target.value)} required minLength={8} className="mt-1 bg-transparent border-app" data-testid="register-confirm-input" />
                      </div>
                      <p className="text-xs t-secondary">{t("auth.pwd_rule")}</p>
                    </>
                  ) : (
                    <>
                      <div>
                        <Label className="t-secondary text-xs uppercase tracking-wider">{t("login.space_name")}</Label>
                        <Input value={groupName} onChange={(e) => setGroupName(e.target.value)} required className="mt-1 bg-transparent border-app" data-testid="register-group-name-input" />
                      </div>
                      <div>
                        <Label className="t-secondary text-xs uppercase tracking-wider">{t("diminutif")}</Label>
                        <Input value={diminutif} onChange={(e) => setDiminutif(e.target.value)} placeholder="ex. bagadkemper" className="mt-1 bg-transparent border-app" data-testid="register-diminutif-input" />
                      </div>
                      <div>
                        <Label className="t-secondary text-xs uppercase tracking-wider">{t("logo_optional")}</Label>
                        <Input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} className="mt-1 bg-transparent border-app" data-testid="register-logo-input" />
                      </div>
                      <div>
                        <Label className="t-secondary text-xs uppercase tracking-wider">{t("language")}</Label>
                        <Select value={language} onValueChange={setLanguage}>
                          <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="register-language-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fr">Français</SelectItem>
                            <SelectItem value="en">English</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                  <Button
                    type="button"
                    onClick={() => {
                      setError("");
                      if (spaceKind !== "school") {
                        if (regPassword !== regConfirm) { setError(t("param.pwd_mismatch")); return; }
                        if (regPassword.length < 8) { setError(t("auth.pwd_rule")); return; }
                      }
                      setRegStep(2);
                    }}
                    disabled={spaceKind === "school" ? !groupName.trim() : (!prenom.trim() || !regEmail.trim() || !regPassword || !regConfirm)}
                    className="w-full brand-bg text-white" data-testid="register-next-button">
                    {t("next")}
                  </Button>
                </>
              ) : spaceKind === "band" ? (
                <>
                  <h2 className="font-heading font-bold text-lg">{t("auth.your_group")}</h2>
                  <div>
                    <Label className="t-secondary text-xs uppercase tracking-wider">{t("group_name")}</Label>
                    <Input value={groupName} onChange={(e) => setGroupName(e.target.value)} required className="mt-1 bg-transparent border-app" data-testid="register-group-name-input" />
                  </div>
                  <div>
                    <Label className="t-secondary text-xs uppercase tracking-wider">{t("diminutif")}</Label>
                    <Input value={diminutif} onChange={(e) => setDiminutif(e.target.value)} placeholder="ex. bagadkemper" className="mt-1 bg-transparent border-app" data-testid="register-diminutif-input" />
                  </div>
                  <div>
                    <Label className="t-secondary text-xs uppercase tracking-wider">{t("style_label")}</Label>
                    <Select value={style} onValueChange={setStyle}>
                      <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="register-style-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pipeband">Pipe Band</SelectItem>
                        <SelectItem value="bagad">Bagad</SelectItem>
                        <SelectItem value="autre">{t("cat.autre")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {style === "autre" && (
                    <div>
                      <Label className="t-secondary text-xs uppercase tracking-wider">{t("custom_style")}</Label>
                      <Input value={customStyle} onChange={(e) => setCustomStyle(e.target.value)} placeholder={t("login.custom_style_placeholder")} className="mt-1 bg-transparent border-app" data-testid="register-custom-style-input" />
                    </div>
                  )}
                  <div>
                    <Label className="t-secondary text-xs uppercase tracking-wider">{t("logo_optional")}</Label>
                    <Input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} className="mt-1 bg-transparent border-app" data-testid="register-logo-input" />
                  </div>
                  <div>
                    <Label className="t-secondary text-xs uppercase tracking-wider">{t("language")}</Label>
                    <Select value={language} onValueChange={setLanguage}>
                      <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="register-language-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fr">Français</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="t-secondary text-xs uppercase tracking-wider">{t("instrument_label")}</Label>
                    {instruments.length > 0 ? (
                      <Select value={instrument} onValueChange={setInstrument}>
                        <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="register-instrument-select"><SelectValue placeholder={t("usr.choose")} /></SelectTrigger>
                        <SelectContent>
                          {instruments.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input value={instrument} onChange={(e) => setInstrument(e.target.value)} placeholder="ex. Trompette" className="mt-1 bg-transparent border-app" data-testid="register-instrument-input" />
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" className="border-app" onClick={() => setRegStep(1)} data-testid="register-back-button">{t("back")}</Button>
                    <Button type="submit" disabled={loading} className="flex-1 brand-bg text-white" data-testid="register-submit-button">
                      {loading ? t("creating") : t("auth.create_account")}
                    </Button>
                  </div>
                </>
              ) : spaceKind === "personal" ? (
                <>
                  <h2 className="font-heading font-bold text-lg">{t("login.space_personal")}</h2>
                  <p className="text-sm t-secondary" data-testid="register-personal-info">{t("space.personal_ready")}</p>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" className="border-app" onClick={() => setRegStep(1)} data-testid="register-back-button">{t("back")}</Button>
                    <Button type="submit" disabled={loading} className="flex-1 brand-bg text-white" data-testid="register-submit-button">
                      {loading ? t("creating") : t("auth.create_account")}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="font-heading font-bold text-lg">{t("login.teacher_profile")}</h2>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="t-secondary text-xs uppercase tracking-wider">{t("firstname")}</Label>
                      <Input value={prenom} onChange={(e) => setPrenom(e.target.value)} required className="mt-1 bg-transparent border-app" data-testid="register-prenom-input" />
                    </div>
                    <div>
                      <Label className="t-secondary text-xs uppercase tracking-wider">{t("lastname")}</Label>
                      <Input value={nom} onChange={(e) => setNom(e.target.value)} className="mt-1 bg-transparent border-app" data-testid="register-nom-input" />
                    </div>
                  </div>
                  <div>
                    <Label className="t-secondary text-xs uppercase tracking-wider">{t("password_label")}</Label>
                    <Input type="password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} required minLength={4} className="mt-1 bg-transparent border-app" data-testid="register-password-input" />
                  </div>
                  <div>
                    <Label className="t-secondary text-xs uppercase tracking-wider">{t("instrument_label")}</Label>
                    <Input value={instrument} onChange={(e) => setInstrument(e.target.value)} placeholder="ex. Cornemuse" className="mt-1 bg-transparent border-app" data-testid="register-instrument-input" />
                  </div>
                  <p className="text-xs t-secondary">{t("your_id_will_be")} <span className="brand font-mono">{(prenom || "prenom").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")}@{(diminutif || groupName || "groupe").toLowerCase().normalize("NFD").replace(/[^a-z0-9]+/g, "")}</span></p>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" className="border-app" onClick={() => setRegStep(1)} data-testid="register-back-button">{t("back")}</Button>
                    <Button type="submit" disabled={loading} className="flex-1 brand-bg text-white" data-testid="register-submit-button">
                      {loading ? t("creating") : t("create_group")}
                    </Button>
                  </div>
                </>
              )}
            </form>
          )}

          {mode === "forgot" && (
            <form onSubmit={doForgot} className="app-card p-6 space-y-4 anim-fade-up" data-testid="forgot-form">
              <h2 className="font-heading font-bold text-lg">{t("auth.forgot_title")}</h2>
              <p className="text-sm t-secondary">{t("auth.forgot_sub")}</p>
              {forgotSent ? (
                <div className="space-y-4" data-testid="forgot-sent">
                  <p className="text-sm text-emerald-400">{t("auth.forgot_sent")}</p>
                  <Button type="button" variant="outline" className="w-full border-app"
                    onClick={() => { setMode("login"); setForgotSent(false); setError(""); }}
                    data-testid="forgot-back-login">
                    {t("auth.back_to_login")}
                  </Button>
                </div>
              ) : (
                <>
                  <div>
                    <Label className="t-secondary text-xs uppercase tracking-wider">{t("auth.email_label")}</Label>
                    <Input type="email" inputMode="email" autoComplete="email" value={loginId} onChange={(e) => setLoginId(e.target.value)} required
                      placeholder={t("auth.email_ph")} className="mt-1 bg-transparent border-app" data-testid="forgot-email-input" />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full brand-bg text-white hover:opacity-90" data-testid="forgot-submit-button">
                    {loading ? t("connecting") : t("auth.send_link")}
                  </Button>
                  <button type="button" onClick={() => { setMode("login"); setError(""); }} className="w-full text-xs t-secondary hover:text-white" data-testid="forgot-back-link">
                    {t("auth.back_to_login")}
                  </button>
                </>
              )}
            </form>
          )}

          {mode === "reset" && (
            <form onSubmit={doReset} className="app-card p-6 space-y-4 anim-fade-up" data-testid="reset-form">
              <h2 className="font-heading font-bold text-lg">{t("auth.reset_title")}</h2>
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("auth.new_password")}</Label>
                <Input type="password" autoComplete="new-password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} required minLength={8}
                  className="mt-1 bg-transparent border-app" data-testid="reset-password-input" />
              </div>
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("confirmation")}</Label>
                <Input type="password" autoComplete="new-password" value={newPwdConfirm} onChange={(e) => setNewPwdConfirm(e.target.value)} required minLength={8}
                  className="mt-1 bg-transparent border-app" data-testid="reset-confirm-input" />
              </div>
              <p className="text-xs t-secondary">{t("auth.pwd_rule")}</p>
              <Button type="submit" disabled={loading} className="w-full brand-bg text-white hover:opacity-90" data-testid="reset-submit-button">
                {loading ? t("connecting") : t("auth.reset_save")}
              </Button>
              <button type="button" onClick={() => { setMode("forgot"); setForgotSent(false); setError(""); }} className="w-full text-xs t-secondary hover:text-white" data-testid="reset-ask-again">
                {t("auth.reset_ask_again")}
              </button>
            </form>
          )}

          {mode === "verify" && (
            <form onSubmit={doVerify} className="app-card p-6 space-y-4 anim-fade-up" data-testid="verify-form">
              <h2 className="font-heading font-bold text-lg">{t("auth.verify_title")}</h2>
              <p className="text-sm t-secondary">{t("auth.verify_sub")}</p>
              <p className="text-sm font-mono brand" data-testid="verify-masked-email">{maskedEmail}</p>
              <div className="flex gap-2 justify-center" data-testid="otp-inputs">
                {otpDigits.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => (otpRefs.current[i] = el)}
                    value={d}
                    onChange={(e) => onOtpChange(i, e.target.value)}
                    onKeyDown={(e) => onOtpKeyDown(i, e)}
                    inputMode="numeric"
                    autoComplete={i === 0 ? "one-time-code" : "off"}
                    maxLength={1}
                    aria-label={`${t("auth.code_label")} ${i + 1}`}
                    className="w-11 h-12 text-center text-xl font-bold rounded-lg border border-app bg-transparent focus:border-emerald-500 outline-none transition-colors"
                    data-testid={`otp-digit-${i}`}
                  />
                ))}
              </div>
              <Button type="submit" disabled={loading} className="w-full brand-bg text-white hover:opacity-90" data-testid="verify-submit-button">
                {loading ? t("connecting") : t("auth.confirm")}
              </Button>
              <div className="flex items-center justify-between text-sm">
                <button type="button" className="text-xs t-secondary hover:text-white" onClick={() => { setMode(afterVerify ? "register" : "login"); setError(""); }} data-testid="verify-back-link">
                  {t("back")}
                </button>
                <button type="button" onClick={doResend} disabled={resendIn > 0} className="text-xs brand hover:underline disabled:opacity-40" data-testid="resend-code-button">
                  {resendIn > 0 ? t("auth.resend_wait", { s: resendIn }) : t("auth.resend")}
                </button>
              </div>
            </form>
          )}

          {mode === "choose" && (
            <div className="app-card p-6 space-y-3 anim-fade-up" data-testid="choose-group">
              <h2 className="font-heading font-bold text-lg">{t("auth.my_groups")}</h2>
              <p className="text-sm t-secondary">{t("auth.choose_group_sub")}</p>
              {memberships.map((m) => (
                <button key={m.membership_id} onClick={() => doSelect(m)} disabled={loading}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-app hover:bg-white/5 text-left transition-colors"
                  data-testid={`choose-membership-${m.membership_id}`}>
                  <div className="h-10 w-10 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                    <Music2 className="h-5 w-5 t-secondary" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{m.group_kind === "personal" ? t("layout.my_space") : m.group_name}</p>
                    <p className="text-xs t-secondary truncate">{m.prenom} {m.nom}{m.instrument ? ` · ${m.instrument}` : ""}</p>
                  </div>
                </button>
              ))}
              <button type="button" onClick={() => { setSpaceKind("band"); setMode("create-group"); }} className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed border-app hover:bg-white/5 text-sm brand transition-colors" data-testid="choose-create-space">
                {t("auth.create_space")}
              </button>
              <button type="button" onClick={quitIdentitySession} className="w-full text-xs t-secondary hover:text-white pt-1" data-testid="choose-logout">
                {t("common.logout")}
              </button>
            </div>
          )}

          {mode === "create-group" && (
            <form onSubmit={(e) => { e.preventDefault(); doCreateGroup(); }} className="app-card p-6 space-y-4 anim-fade-up" data-testid="create-group-form">
              <h2 className="font-heading font-bold text-lg">{t("auth.no_group_title")}</h2>
              <p className="text-sm t-secondary">{t("auth.no_group_sub")}</p>
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("login.space_type_label")}</Label>
                <div className="grid grid-cols-3 gap-2 mt-1" data-testid="create-group-space-type">
                  {[["band", "login.space_band", "login.space_band_desc", false], ["school", "login.space_school", "login.space_school_desc", true], ["personal", "login.space_personal", "login.space_personal_desc", false]].map(([k, l, d, locked]) => (
                    <button type="button" key={k} disabled={locked} onClick={() => !locked && setSpaceKind(k)}
                      className={`min-w-0 px-2 py-2.5 rounded-lg border text-left transition-colors ${locked ? "border-app t-secondary opacity-60 cursor-not-allowed" : spaceKind === k ? "brand-bg text-white border-transparent" : "border-app t-secondary hover:bg-white/5"}`}
                      data-testid={`choose-space-type-${k}`}>
                      <span lang={lang} className="block text-[13px] font-semibold leading-tight break-words [hyphens:auto]">{t(l)}</span>
                      <span className={`block text-[10px] leading-snug break-words mt-0.5 ${spaceKind === k ? "text-white/80" : "t-secondary"}`}>{t(d)}</span>
                      {locked && <span className="inline-block mt-1 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400" data-testid="choose-space-type-dev-badge">{t("login.space_dev")}</span>}
                    </button>
                  ))}
                </div>
              </div>
              {spaceKind === "school" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="t-secondary text-xs uppercase tracking-wider">{t("firstname")}</Label>
                    <Input value={prenom} onChange={(e) => setPrenom(e.target.value)} required className="mt-1 bg-transparent border-app" data-testid="choose-space-prenom-input" />
                  </div>
                  <div>
                    <Label className="t-secondary text-xs uppercase tracking-wider">{t("lastname")}</Label>
                    <Input value={nom} onChange={(e) => setNom(e.target.value)} className="mt-1 bg-transparent border-app" data-testid="choose-space-nom-input" />
                  </div>
                </div>
              )}
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("group_name")}</Label>
                <Input value={groupName} onChange={(e) => setGroupName(e.target.value)} required className="mt-1 bg-transparent border-app" data-testid="create-group-name-input" />
              </div>
              {spaceKind === "band" && (
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("style_label")}</Label>
                <Select value={style} onValueChange={setStyle}>
                  <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="create-group-style-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pipeband">Pipe Band</SelectItem>
                    <SelectItem value="bagad">Bagad</SelectItem>
                    <SelectItem value="autre">{t("cat.autre")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              )}
              {spaceKind === "band" && style === "autre" && (
                <div>
                  <Label className="t-secondary text-xs uppercase tracking-wider">{t("custom_style")}</Label>
                  <Input value={customStyle} onChange={(e) => setCustomStyle(e.target.value)} placeholder={t("login.custom_style_placeholder")} className="mt-1 bg-transparent border-app" data-testid="create-group-custom-style-input" />
                </div>
              )}
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="border-app" onClick={quitIdentitySession} data-testid="create-group-logout">{t("common.logout")}</Button>
                <Button type="submit" disabled={loading || !groupName.trim()} className="flex-1 brand-bg text-white" data-testid="create-group-submit-button">
                  {loading ? t("creating") : t("create_group")}
                </Button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
}
